import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, ArrowLeft, Sparkles, Stars } from 'lucide-react';
import { authApi } from '../services/api';
import { useNamespacedTranslation } from '../i18n/index';
import { MixedText } from '../utils/rtl';
import SpaceBackground from './layout/SpaceBackground';
import { AuthContainer } from './layout/PageContainer';
import { SpaceCard, Card, CardHeader, CardTitle, CardContent, CardFooter } from './ui/Card';
import { Button } from './ui/Button';
import { LoadingSpinner } from './ui/LoadingSpinner';

const ForgotPassword: React.FC = () => {
  const { t, i18n } = useNamespacedTranslation('auth');
  const navigate = useNavigate();
  const isRTL = i18n.language === 'he';
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    // Basic email validation
    if (!email.trim()) {
      setError(t('forgot_password.errors.email_required'));
      setLoading(false);
      return;
    }

    if (!email.includes('@')) {
      setError(t('login.errors.invalid_credentials'));
      setLoading(false);
      return;
    }

    try {
      const response = await authApi.forgotPassword({ email: email.trim() });
      setSuccess(response.message || t('forgot_password.success.message'));
      setEmail(''); // Clear form on success
    } catch (err: any) {
      setError(err.response?.data?.detail || t('forgot_password.errors.network_error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Space Background */}
      <SpaceBackground theme="mixed" intensity="cosmic" />
      
      {/* Floating Elements */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-20 right-16 animate-float">
          <Sparkles className="w-8 h-8 text-muted-purple/40" />
        </div>
        <div className="absolute top-32 left-20 animate-float-delayed">
          <Stars className="w-10 h-10 text-vibrant-orange/30" />
        </div>
        <div className="absolute bottom-32 right-32 animate-pulse">
          <div className="w-3 h-3 bg-vibrant-orange/50 rounded-full"></div>
        </div>
      </div>

      {/* Main Content */}
      <AuthContainer className="relative z-10">
        <div className="max-w-md w-full mx-auto space-y-8">
          {/* Header */}
          <div className="text-center space-y-6">
            {/* Logo with glow effect */}
            <div className="flex justify-center">
              <div className="relative group">
                <div className="absolute inset-0 bg-vibrant-orange/20 rounded-full blur-xl group-hover:blur-2xl transition-all duration-300 animate-pulse"></div>
                <img 
                  src="/study-bel-v2.png" 
                  alt="StudyBel Logo" 
                  className="relative w-20 h-20 object-contain group-hover:scale-110 transition-transform duration-300"
                />
              </div>
            </div>
            
            {/* Title Text */}
            <div className="space-y-3">
              <h1 className="text-4xl md:text-5xl font-bold text-bg-primary text-center">
                {isRTL ? (
                  <MixedText className="text-4xl md:text-5xl font-bold text-bg-primary">
                    {`${t('forgot_password.title')}`}
                  </MixedText>
                ) : (
                  <>🔐 {t('forgot_password.title')}</>
                )}
              </h1>
              <p className="text-lg md:text-xl text-bg-secondary text-center leading-relaxed">
                <MixedText className="text-lg md:text-xl text-bg-secondary">
                  {`${t('forgot_password.subtitle')}`}
                </MixedText>
              </p>
            </div>
          </div>

          {/* Forgot Password Form */}
          <SpaceCard 
            constellation="ursa" 
            className="group hover:shadow-2xl transition-all duration-500 transform hover:scale-[1.02]"
          >
            <CardHeader>
              <CardTitle className={`text-center text-xl ${isRTL ? 'text-right' : ''}`}>
                {t('forgot_password.title')}
              </CardTitle>
            </CardHeader>

            <form onSubmit={handleSubmit}>
              <CardContent className="space-y-6">
                {/* Success Message */}
                {success && (
                  <Card className="border-vibrant-orange/50 bg-vibrant-orange/10">
                    <CardContent className="text-vibrant-orange text-center py-4">
                      ✅ {success}
                    </CardContent>
                  </Card>
                )}

                {/* Error Message */}
                {error && (
                  <Card className="border-red-500/50 bg-red-500/10 animate-shake">
                    <CardContent className="text-red-400 text-center py-3">
                      ⚠️ {error}
                    </CardContent>
                  </Card>
                )}

                {/* Email Field */}
                <div className="space-y-2">
                  <label 
                    htmlFor="email" 
                    className={`block text-sm font-medium text-surface-secondary ${isRTL ? 'text-right' : ''}`}
                  >
                    {t('forgot_password.form.email')}
                  </label>
                  <div className="relative group">
                    <div className={`absolute inset-y-0 ${isRTL ? 'right-0 pr-3' : 'left-0 pl-3'} flex items-center pointer-events-none transition-colors duration-200`}>
                      <Mail className="h-5 w-5 text-surface-muted group-focus-within:text-vibrant-orange" />
                    </div>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className={`block w-full ${isRTL ? 'pr-10 pl-3 text-right' : 'pl-10 pr-3'} py-3 border-2 border-surface-muted/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-vibrant-orange/50 focus:border-vibrant-orange transition-all duration-200 bg-surface/80 backdrop-blur-sm text-surface-primary placeholder:text-surface-muted hover:border-surface-muted/50`}
                      placeholder={t('forgot_password.form.email_placeholder')}
                      dir={isRTL ? 'rtl' : 'ltr'}
                    />
                  </div>
                </div>
              </CardContent>

              <CardFooter className="flex flex-col space-y-4">
                {/* Submit Button */}
                <Button
                  type="submit"
                  variant="gradient"
                  size="lg"
                  className="w-full"
                  disabled={loading || !!success}
                  glow
                >
                  {loading ? (
                    <div className="flex items-center justify-center space-x-2">
                      <LoadingSpinner variant="simple" size="sm" color="orange" />
                      <span>📧 {t('forgot_password.form.sending')}</span>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center space-x-2">
                      <span>📧 {t('forgot_password.form.submit')}</span>
                    </div>
                  )}
                </Button>

                {/* Back to Login Link */}
                <div className="w-full text-center">
                  <Button
                    variant="cosmic"
                    size="md"
                    className="w-full"
                    onClick={() => navigate('/login')}
                  >
                    <div className={`flex items-center justify-center ${isRTL ? 'flex-row-reverse' : ''} space-x-2`}>
                      <ArrowLeft className="w-4 h-4" />
                      <span>{t('forgot_password.links.back_to_login')}</span>
                    </div>
                  </Button>
                </div>
              </CardFooter>
            </form>
          </SpaceCard>
        </div>
      </AuthContainer>
    </div>
  );
};

export default ForgotPassword; 